import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, LogOut, Trash2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import CollectionManager from "@/components/admin/CollectionManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

function ImageField({ value, onChange, testid }) {
  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="space-y-3">
      {value && (
        <img
          src={value}
          alt="preview"
          className="h-32 w-auto border border-line object-cover"
          data-testid={`${testid}-preview`}
        />
      )}
      <input
        value={value?.startsWith("data:") ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="/images/hero.jpg or https://..."
        className="w-full border border-line bg-sand px-3 py-2 text-sm focus:border-pine focus:ring-0"
        data-testid={testid}
      />
      <input
        type="file"
        accept="image/*"
        onChange={pick}
        className="text-xs text-ink/60"
        data-testid={`${testid}-upload`}
      />
    </div>
  );
}

function ContentEditor() {
  const [sections, setSections] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState("");

  const load = () =>
    axios.get(`${API}/content`).then((r) => {
      setSections(r.data.sections);
      setEdits(JSON.parse(JSON.stringify(r.data.sections)));
    });

  useEffect(() => {
    load();
  }, []);

  const setField = (section, field, value) =>
    setEdits((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));

  const save = async (section) => {
    setSaving(section);
    try {
      await axios.put(`${API}/admin/content/${section}`, edits[section], { withCredentials: true });
      toast.success(`${label(section)} saved`);
    } catch {
      toast.error("Save failed. Please sign in again.");
    } finally {
      setSaving("");
    }
  };

  if (!sections) return <p className="py-12 text-sm text-ink/50">Loading content...</p>;

  return (
    <Accordion type="multiple" className="w-full" data-testid="content-editor">
      {Object.entries(edits).map(([section, fields]) => (
        <AccordionItem key={section} value={section} className="border-line">
          <AccordionTrigger
            className="font-display text-lg tracking-tight text-pine hover:no-underline"
            data-testid={`admin-section-${section}`}
          >
            {label(section)}
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 pb-4 pr-2">
              {Object.entries(fields).map(([field, value]) => (
                <div key={field}>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/60">
                    {label(field)}
                  </label>
                  {field === "image" || field.endsWith("_image") ? (
                    <ImageField
                      value={value}
                      onChange={(v) => setField(section, field, v)}
                      testid={`admin-field-${section}-${field}`}
                    />
                  ) : String(value).length > 80 || String(value).includes("\n") ? (
                    <textarea
                      value={value}
                      rows={Math.min(12, String(value).split("\n").length + 1)}
                      onChange={(e) => setField(section, field, e.target.value)}
                      className="w-full border border-line bg-sand px-3 py-2 text-sm leading-relaxed focus:border-pine focus:ring-0"
                      data-testid={`admin-field-${section}-${field}`}
                    />
                  ) : (
                    <input
                      value={value}
                      onChange={(e) => setField(section, field, e.target.value)}
                      className="w-full border border-line bg-sand px-3 py-2 text-sm focus:border-pine focus:ring-0"
                      data-testid={`admin-field-${section}-${field}`}
                    />
                  )}
                  {(field === "items" ||
                    field === "groups" ||
                    field === "quarters" ||
                    field === "stages" ||
                    field === "steps" ||
                    field === "points") && (
                    <p className="mt-1 text-xs text-ink/40">
                      One entry per line. Separate parts with a pipe: Title|Description|Image path|Icon (icons: bot,
                      factory, trending, graduation, landmark, presentation, briefcase, globe, lightbulb, users,
                      handshake, mic, earth)
                    </p>
                  )}
                  {field === "title_lines" && <p className="mt-1 text-xs text-ink/40">One headline line per row.</p>}
                </div>
              ))}
              <button
                onClick={() => save(section)}
                disabled={saving === section}
                className="btn-sweep bg-pine px-6 py-3 text-sm font-semibold text-sand disabled:opacity-60"
                data-testid={`admin-save-${section}`}
              >
                {saving === section ? "Saving..." : `Save ${label(section)}`}
              </button>
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function EnquiriesPanel() {
  const [items, setItems] = useState(null);

  const load = () =>
    axios.get(`${API}/admin/enquiries`, { withCredentials: true }).then((r) => setItems(r.data.enquiries));

  useEffect(() => {
    load().catch(() => setItems([]));
  }, []);

  const toggleRead = async (id, read) => {
    await axios.patch(`${API}/admin/enquiries/${id}`, { read: !read }, { withCredentials: true });
    load();
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/enquiries/${id}`, { withCredentials: true });
    toast.success("Enquiry deleted");
    load();
  };

  if (!items) return <p className="py-12 text-sm text-ink/50">Loading enquiries...</p>;
  if (!items.length)
    return (
      <p className="border border-line bg-bone p-10 text-sm text-ink/60" data-testid="enquiries-empty">
        No enquiries yet. New submissions from the contact form will appear here.
      </p>
    );

  return (
    <div className="space-y-4" data-testid="enquiries-list">
      {items.map((e) => (
        <div
          key={e.id}
          className={`border p-6 ${e.read ? "border-line bg-sand opacity-70" : "border-pine bg-bone"}`}
          data-testid={`enquiry-${e.id}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-display text-lg text-pine">
                {e.name} <span className="text-sm text-ink/50">({e.role})</span>
              </p>
              <p className="mt-1 text-sm text-ink/70">
                <a href={`mailto:${e.email}`} className="underline underline-offset-2">
                  {e.email}
                </a>
                {e.organisation ? ` — ${e.organisation}` : ""}
              </p>
              <p className="mt-1 text-xs text-ink/40">{new Date(e.created_at).toLocaleString()}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => toggleRead(e.id, e.read)}
                className="border border-pine px-4 py-2 text-xs font-semibold text-pine transition-colors hover:bg-pine hover:text-sand"
                data-testid={`enquiry-read-${e.id}`}
              >
                {e.read ? "Mark unread" : "Mark read"}
              </button>
              <button
                onClick={() => remove(e.id)}
                className="border border-terra px-4 py-2 text-xs font-semibold text-terra transition-colors hover:bg-terra hover:text-white"
                data-testid={`enquiry-delete-${e.id}`}
                aria-label="Delete enquiry"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          {e.message && (
            <p className="mt-4 border-t border-line pt-4 text-sm leading-relaxed text-ink/80">{e.message}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState("content");

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setAdmin(r.data))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/admin/login");
  };

  if (!admin)
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand">
        <LogoMark className="h-10 w-10 animate-pulse text-pine" />
      </div>
    );

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <header className="border-b border-line bg-bone">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8 text-pine" />
            <div>
              <p className="font-display text-lg font-bold tracking-tight text-pine">Veridian CMS</p>
              <p className="text-xs text-ink/50">{admin.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 border border-pine px-4 py-2 text-xs font-semibold text-pine transition-colors hover:bg-pine hover:text-sand"
              data-testid="admin-view-site"
            >
              View site <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 border border-terra px-4 py-2 text-xs font-semibold text-terra transition-colors hover:bg-terra hover:text-white"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-6 py-10">
        <div className="mb-8 flex flex-wrap gap-2">
          {[
            { id: "content", label: "Site content" },
            { id: "board", label: "Founding board" },
            { id: "events", label: "Events & news" },
            { id: "spotlights", label: "Spotlights" },
            { id: "enquiries", label: "Enquiries" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === t.id ? "bg-pine text-white" : "border border-line text-ink/60 hover:border-pine"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "content" && <ContentEditor />}
        {tab === "enquiries" && <EnquiriesPanel />}
        {["board", "events", "spotlights"].includes(tab) && <CollectionManager key={tab} name={tab} />}
      </div>
    </div>
  );
}
